import React, { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext.js";
import "./styles/Profile.css";

const STAFF_ROLES = ["admin", "journalist", "reporter"];

const Profile = () => {
  const { user, token } = useContext(AuthContext);
  const isStaff = STAFF_ROLES.includes(user?.role);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!token || isStaff) {
      setLoading(false);
      setInvoicesLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const [subResp, billResp] = await Promise.all([
          fetch("/api/subscriptions/my-subscription", { headers: { Authorization: `Bearer ${token}` } }),
          fetch("/api/subscriptions/billing-history", { headers: { Authorization: `Bearer ${token}` } })
        ]);
        const subData = await subResp.json();
        setSubscription(subData.subscription);
        const billData = await billResp.json();
        setInvoices(billData.invoices || []);
      } catch (err) {
        console.error("Error fetching profile data:", err);
      } finally {
        setLoading(false);
        setInvoicesLoading(false);
      }
    };
    fetchData();
  }, [token, isStaff]);

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.")) return;
    setCancelLoading(true);
    setMessage(null);
    try {
      const resp = await fetch("/api/subscriptions/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message);
      setMessage("Your subscription will cancel at the end of the current billing period.");
      setSubscription(prev => prev ? { ...prev, cancel_at_period_end: true } : prev);
    } catch (err) {
      setMessage(err.message);
    } finally {
      setCancelLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "\u2014";
    const d = typeof dateString === "number" ? new Date(dateString * 1000) : new Date(dateString);
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  };

  const formatCurrency = (cents) => {
    if (!cents && cents !== 0) return "\u2014";
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: "Administrator",
      journalist: "Journalist",
      reporter: "Reporter",
      user: "Subscriber",
      freemium: "Free Account"
    };
    return labels[role] || role;
  };

  const getStatusBadge = (status) => {
    const classes = {
      paid: "profile-status profile-status-active",
      open: "profile-status profile-status-pending",
      void: "profile-status profile-status-canceled",
      uncollectible: "profile-status profile-status-canceled",
      draft: "profile-status profile-status-pending"
    };
    return classes[status] || "profile-status";
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        <h1>My Profile</h1>

        <div className="profile-section">
          <h2>Account Overview</h2>
          <div className="profile-info-grid">
            <div className="profile-info-item">
              <span className="profile-info-label">Username</span>
              <span className="profile-info-value">{user.username}</span>
            </div>
            <div className="profile-info-item">
              <span className="profile-info-label">Account Type</span>
              <span className="profile-info-value">{getRoleLabel(user.role)}</span>
            </div>
          </div>
        </div>

        {!isStaff && (
          <div className="profile-section">
            <h2>Subscription</h2>
            {loading ? (
              <p className="profile-loading">Loading subscription details...</p>
            ) : subscription && (subscription.status === "active" || subscription.status === "trialing") ? (
              <div className="subscription-details">
                <div className="profile-info-grid">
                  <div className="profile-info-item">
                    <span className="profile-info-label">Plan</span>
                    <span className="profile-info-value">{subscription.plan_name}</span>
                  </div>
                  <div className="profile-info-item">
                    <span className="profile-info-label">Status</span>
                    <span className={`profile-status profile-status-${subscription.status}`}>
                      {subscription.cancel_at_period_end ? "Canceling" : subscription.status === "active" ? "Active" : subscription.status}
                    </span>
                  </div>
                  <div className="profile-info-item">
                    <span className="profile-info-label">Amount</span>
                    <span className="profile-info-value">
                      {formatCurrency(subscription.special_price_cents || subscription.price_cents)} / {subscription.interval_label || "period"}
                    </span>
                  </div>
                  <div className="profile-info-item">
                    <span className="profile-info-label">Next Billing Date</span>
                    <span className="profile-info-value">
                      {subscription.cancel_at_period_end ? "N/A (Canceling)" : formatDate(subscription.current_period_end)}
                    </span>
                  </div>
                  <div className="profile-info-item">
                    <span className="profile-info-label">Member Since</span>
                    <span className="profile-info-value">{formatDate(subscription.member_since || subscription.created_at)}</span>
                  </div>
                </div>

                {message && <p className="profile-message">{message}</p>}

                <div className="subscription-actions">
                  {!subscription.cancel_at_period_end && (
                    <button onClick={handleCancel} disabled={cancelLoading} className="cancel-subscription-btn">
                      {cancelLoading ? "Processing..." : "Cancel Subscription"}
                    </button>
                  )}
                  {subscription.cancel_at_period_end && (
                    <p className="cancel-note">Your access continues until {formatDate(subscription.current_period_end)}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="no-subscription">
                <p>You don't have an active subscription.</p>
                {(user.role === "freemium" || (!subscription || subscription.status === "canceled")) && (
                  <Link to="/subscribe" className="subscribe-cta-btn">View Plans & Subscribe</Link>
                )}
              </div>
            )}
          </div>
        )}

        {!isStaff && (
          <div className="profile-section">
            <h2>Billing History</h2>
            {invoicesLoading ? (
              <p className="profile-loading">Loading billing history...</p>
            ) : invoices.length === 0 ? (
              <div className="no-subscription">
                <p>No billing history available.</p>
              </div>
            ) : (
              <div className="billing-history">
                <table className="billing-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Period</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id}>
                        <td>{formatDate(inv.date)}</td>
                        <td>{inv.description}</td>
                        <td>{formatCurrency(inv.amount)}</td>
                        <td>
                          <span className={getStatusBadge(inv.status)}>
                            {inv.status === "paid" ? "Paid" : inv.status}
                          </span>
                        </td>
                        <td className="billing-period">
                          {inv.period_start && inv.period_end
                            ? `${formatDate(inv.period_start)} – ${formatDate(inv.period_end)}`
                            : "\u2014"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
